import { useRef, useEffect } from 'react';
import { Stack, useTheme } from '@mui/material';
import { useReducer } from "hooks";
import { useOnlineBookingApi, useOnlineBookingPopup } from 'shell';

import { CustomMessage, SvgPlaceholder } from 'components/core';
import { StepCustomWidgets } from 'components';
import { defaultStepPropertyTypeListEnum, journeyTypes } from 'assets/constants';
import { IconGreat, IconHug } from 'assets/icons';
// import { IconEmail } from 'assets/icons';
import { connect } from 'react-redux';

import { SuccessRoot, StyledShadowBox, /*Confirmation,*/ WhiteButton, TextButton } from './styles';
import { dateLanguageFormat, findProperty, replaceSubstrings, separateWidgetsAndProperties, timeLanguageFormat, toTime } from 'utils';
import { CancelAppointment } from 'components';
import { useAnalyticsEventPageView } from 'screens/hooks';
import { isDeepLink } from 'utils/src/journeyUtils';

const { success, cancelMessage, cancelSuccess, successReschedule } = defaultStepPropertyTypeListEnum;

const Success = ({ lngCode, step, cancelAppointmentData, scriptMessages, handleBackToFirstStep, userData, cancelStepActive, journeyTypeId }) => {
  useAnalyticsEventPageView("Success")
  const [api, MainPopup, theme] = [useOnlineBookingApi(), useOnlineBookingPopup(), useTheme()];

  const ref = useRef();
  useEffect(() => ref.current && ref.current.focus())

  // Separate the widgets and main properties
  const [widgetsBefore, allMainProperties, widgetsAfter] = separateWidgetsAndProperties(step?.properties);

  // get all user data gathered through the steps
  const { customerName, appointmentType, service, location, dateAndTime } = userData;

  // Fetch the cancelation reasons
  const { isLoading: cancelationReasonsLoading, data: cancelationReasons } = api.org.useCustomerCancelationReasons();

  const successProperty = journeyTypeId === journeyTypes.journey ? findProperty(allMainProperties, success) : findProperty(allMainProperties, successReschedule)
  const { messageHeader, message, buttonBookAnotherAppointment } = successProperty;
  // Extract cancelPopup, and cancelSuccess from cancelAppointmentData
  const cancelAppointmentDataProperties = cancelAppointmentData?.properties;
  const cancelPopup = findProperty(cancelAppointmentDataProperties, cancelMessage);
  const buttonCancelAppointment = () => cancelPopup?.buttonCancelAppointment;

  const { date, appointmentStartTime } = dateAndTime;
  const fullDateAndTime = `${date} ${toTime(parseInt(appointmentStartTime / 60), parseInt(appointmentStartTime % 60))}`;


  let timeToCancelation = 0;
  if (!!cancelAppointmentData) {
    const cancelationLeadTime = cancelPopup.cancelationLeadTime * 60 * 1000;
    //                       CancelTime                        cancelationLeadTime    currentTime
    timeToCancelation = new Date(fullDateAndTime).getTime() - cancelationLeadTime - new Date().getTime();
  }

  useEffect(() => {
    !!cancelAppointmentData && (timeToCancelation > 0) && setTimeout(() => {
      updateState({ cancelationExpired: true, popupActive: false })
    }, timeToCancelation)
  }, [])

  const [state, updateState] = useReducer({ popupActive: false, cancelationExpired: timeToCancelation <= 0 });
  const { popupActive, cancelationExpired } = state;


  const handleRedirectByJourneyGuide = () => {
    const newUrl = `./#/${userData.journeyGUID}`;
    window.location.replace(newUrl);
  };
  //Keys that will be replaced in the confirmation message
  const replaceKeys = {
    '{{customerName}}': customerName,
    '{{appointmentType}}': appointmentType?.name,
    '{{service}}': service?.name,
    '{{locationName}}': location?.name,
    '{{dateAndTime}}': `${dateLanguageFormat(lngCode, date)}, ${timeLanguageFormat(lngCode, fullDateAndTime)}`
  }

  function confirmationMessage(messageHeader, message) {
    const fullMessage = `<p role="heading" aria-level="1"><strong style="color: ${theme.vars.palette.primary.main}">${messageHeader}</strong></p>` + message;
    return replaceSubstrings(fullMessage, replaceKeys)
  }

  function cancelationMessage() {
    const { message, messageHeader } = findProperty(cancelAppointmentDataProperties, cancelSuccess);
    return confirmationMessage(messageHeader, message);
  }

  const innerStyle = { justifyContent: 'flex-start', overflowY: 'auto', padding: theme.spacing(1), top: '40%' }

  const showCancelButton = !cancelStepActive && !!cancelAppointmentData && !cancelationExpired && !cancelationReasonsLoading;

  return (
    <SuccessRoot>
      <StepCustomWidgets widgets={widgetsBefore} />
      <StyledShadowBox className="BoxShadow" tabIndex={0} ref={ref}>
        <SvgPlaceholder
          icon={!cancelStepActive ? IconGreat : IconHug}
          msg={!cancelStepActive ? confirmationMessage(messageHeader, message) : cancelationMessage()}
          msgProps={{ style: { fontSize: '0.875rem', paddingLeft: '1em', paddingRight: '1em', marginBottom: '1em' } }}
        />
      </StyledShadowBox>
      {scriptMessages?.map(({ message, messageType }, index) => (<CustomMessage key={index} message={message} type={messageType} />))}
      <Stack mb={2}>
        {journeyTypeId === journeyTypes.journey && <WhiteButton size='small' onClick={(e) => {
          isDeepLink() && handleRedirectByJourneyGuide();
          handleBackToFirstStep(e);
        }} title={buttonBookAnotherAppointment} >
          {buttonBookAnotherAppointment}
        </WhiteButton>}
        {showCancelButton &&
          <TextButton size='small' variant={'text'} onClick={() => updateState({ popupActive: true })} title={buttonCancelAppointment()} >
            {buttonCancelAppointment()}
          </TextButton>
        }
      </Stack>
      <StepCustomWidgets widgets={widgetsAfter} />

      {/* Display the popup if active */}
      {popupActive &&
        <MainPopup
          innerStyle={innerStyle}
          container={'BasePosition'}
          open={popupActive}
          closePopup={() => updateState({ popupActive: false })}
        >
          <CancelAppointment
            step={cancelPopup}
            cancelationReasons={cancelationReasons}
            closePopup={() => updateState({ popupActive: false })}
          />
        </MainPopup>
      }

    </SuccessRoot>
  );
}

export default connect(
  ({ current: { steps: { cancelStepActive, scriptMessages }, userData } }) =>
    ({ userData, cancelStepActive, scriptMessages }))
  (Success);